// File: templating.ts
// Description: {{variable}} extraction, rendering and positional ordering shared by the client preview and the server send pipeline.
// Author: Jose-Jorge HERNANDEZ
// Company: Parlee Conseiller, Inc.
// Date: 2026-06-23
// Last edit date: 2026-10-25
// Version: 1.1.0

// Shared between src/client (live preview) and src/server (Phase 6 send pipeline) so
// rendering can never drift between "what you previewed" and "what actually got sent."
const VARIABLE_PATTERN = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

export function extractVariables(body: string): string[] {
  const found = new Set<string>();
  for (const match of body.matchAll(VARIABLE_PATTERN)) {
    found.add(match[1]!);
  }
  return Array.from(found);
}

/** Unresolved variables are left as-is (e.g. "{{company}}") rather than blanked, so a missing value is visible. */
export function renderTemplate(body: string, variables: Record<string, string>): string {
  return body.replace(VARIABLE_PATTERN, (full, key: string) => (key in variables ? variables[key]! : full));
}

/**
 * Picks the values for `body`'s placeholders in order of appearance, so the WhatsApp adapter can
 * map them to Twilio's positional ContentVariables ({{1}}, {{2}}, ...). Placeholders with no value
 * are returned in `missing` instead of being skipped silently: with a Content Template, a skipped
 * value makes Twilio fall back to the template's sample text (e.g. the same name on every message).
 */
export function orderVariables(body: string, variables: Record<string, string>): { ordered: Record<string, string>; missing: string[] } {
  const ordered: Record<string, string> = {};
  const missing: string[] = [];
  for (const key of extractVariables(body)) {
    if (key in variables) ordered[key] = variables[key]!;
    else missing.push(key);
  }
  return { ordered, missing };
}

/** User-facing error for unmatched placeholders, naming the fields that were actually available. */
export function describeMissingVariables(missing: string[], available: string[]): string {
  const placeholders = missing.map((key) => `{{${key}}}`).join(', ');
  const fields = available.length > 0 ? available.join(', ') : 'none';
  return `Placeholder(s) ${placeholders} have no matching value. Placeholders must use the field names (available: ${fields}), not Twilio's {{1}}, {{2}} positions.`;
}
